// src/pages/Produits/Produits.jsx
import { useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { getDocs, collection, getDoc, doc } from 'firebase/firestore';
import { store } from '../../firebase.config';
import Annonce from '../../component/Annonce/Annonce';
import './Produits.css';

import logo from '../../assets/images/cadre1/logoEcoleDavid.png';
import tel   from '../../assets/images/cadre1/phone-call.png';
import Horloge from '../../assets/images/cadre1/Horloge.png';

// ── images catégories ──────────────────────────────
import imgElectrique   from '../../assets/images/Categories/Guitare Electrique.webp';
import imgFolk         from '../../assets/images/Categories/Guitare Folk.webp';
import imgElectroacc   from '../../assets/images/Categories/Guitare Electroaccoustique Bis.webp';
import imgClassique    from '../../assets/images/Categories/Guitare classic.webp';
import imgBasse        from '../../assets/images/Categories/Basse.webp';
import imgGaucher      from '../../assets/images/Categories/Guitare Gaucher.webp';
import imgAmpGuitare   from '../../assets/images/Categories/Ampli Electrique.webp';
import imgAmpBasse     from '../../assets/images/Categories/Ampli basse.webp';
import imgAmpElectroacc from '../../assets/images/Categories/Ampli Electroaccoustique.webp';
import imgAccessoires  from '../../assets/images/Categories/Accesoire Pedale.webp';

// ── constantes ────────────────────────────────────
const CATEGORIES = [
  { value: 'Electrique',    label: 'Électriques',          img: imgElectrique },
  { value: 'Folk',          label: 'Folk',                 img: imgFolk },
  { value: 'Electroacc',    label: 'Électroacoustiques',   img: imgElectroacc },
  { value: 'Classique',     label: 'Classiques',           img: imgClassique },
  { value: 'Basse',         label: 'Basses',               img: imgBasse },
  { value: 'Gaucher',       label: 'Gauchers',             img: imgGaucher },
  { value: 'AmpGuitare',    label: 'Amplis guitare',       img: imgAmpGuitare },
  { value: 'AmpBasse',      label: 'Amplis basse',         img: imgAmpBasse },
  { value: 'AmpElectroacc', label: 'Amplis électroacc.',   img: imgAmpElectroacc },
  { value: 'Accessoires',   label: 'Accessoires',          img: imgAccessoires },
];

const PRIX_OPTIONS = [
  { value: '',       label: 'Tous les prix' },
  { value: '0-100',  label: 'Accessoires (0 – 100 €)' },
  { value: '100-300',label: 'Entrée de gamme (100 – 300 €)' },
  { value: '+300',   label: 'Moyenne gamme (+300 €)' },
  { value: '+700',   label: 'Haute gamme (+700 €)' },
  { value: '+2000',  label: 'Très haute gamme (+2000 €)' },
];

// ─────────────────────────────────────────────────
export default function Produits() {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  // ── état ──────────────────────────────────────
  const [objects,   setObjects]   = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [horaires,  setHoraires]  = useState({});
  const [showCats,  setShowCats]  = useState(false);

  // filtres pilotés par l'URL (?cat=Folk&prix=+300&q=fender)
  const cat      = searchParams.get('cat')  || '';
  const prix     = searchParams.get('prix') || '';
  const search   = searchParams.get('q')    || '';

  const searchRef = useRef(null);

  // ── helpers URL ───────────────────────────────
  const setParam = (key, value) => {
    const next = new URLSearchParams(searchParams);
    if (value) next.set(key, value);
    else        next.delete(key);
    setSearchParams(next, { replace: true });
  };

  const resetFiltres = () => setSearchParams({}, { replace: true });

  // ── chargement Firebase ───────────────────────
  useEffect(() => {
    let mounted = true;
    async function load() {
      setLoading(true);
      const [snap, custom] = await Promise.all([
        getDocs(collection(store, 'object')),
        getDoc(doc(store, 'custom/3OLblya5h9o2STSSkXs8')),
      ]);
      if (!mounted) return;

      const list = snap.docs.map((d) => ({ ...d.data(), id: d.id }));
      setObjects(list.sort((a, b) => (b.date || 0) - (a.date || 0)));
      setHoraires(custom.data()?.horaire || {});
      setLoading(false);
    }
    load();
    return () => { mounted = false; };
  }, []);

  // ── liste filtrée ─────────────────────────────
  const filtered = useMemo(() => {
    let base = [...objects];

    // tri prix
    if (prix) {
      base.sort(
        (a, b) =>
          parseInt(a.prixFix || a.prix || 0, 10) -
          parseInt(b.prixFix || b.prix || 0, 10)
      );
    }

    return base
      .filter((o) => (cat    ? o.categorie === cat  : true))
      .filter((o) => (prix   ? o.prix      === prix : true))
      .filter((o) =>
        search
          ? (o.nom || o.name || o.titre || '').toLowerCase().includes(search.toLowerCase()) ||
            (o.model || '').toLowerCase().includes(search.toLowerCase()) ||
            (o.description || '').toLowerCase().includes(search.toLowerCase())
          : true
      );
  }, [objects, cat, prix, search]);

  // ── label catégorie active ────────────────────
  const catLabel = CATEGORIES.find((c) => c.value === cat)?.label || 'Tous les produits';

  // ─────────────────────────────────────────────
  return (
    <div className="PPage">

      {/* ══ TOPBAR ════════════════════════════════ */}
      <header className="PTop">
        <div className="PTop__brand" onClick={() => navigate('/')}>
          <img src={logo} alt="Logo" className="PTop__logo" />
          <div>
            <div className="PTop__title">SHOP</div>
            <div className="PTop__sub">Instruments d&apos;occasion</div>
          </div>
        </div>

        {/* Barre de recherche */}
        <div className="PTop__searchWrap">
          <span className="PTop__searchIcon">🔍</span>
          <input
            ref={searchRef}
            className="PTop__searchInput"
            placeholder="Rechercher une marque, un modèle…"
            value={search}
            onChange={(e) => setParam('q', e.target.value)}
          />
          {search && (
            <button className="PTop__searchClear" onClick={() => setParam('q', '')}>✕</button>
          )}
        </div>

        <div className="PTop__actions">
          <a className="PTop__action" href="tel:0663460105">
            <img src={tel} alt="" />
            <span>Appeler</span>
          </a>
          <button className="PTop__action" onClick={() => navigate('/', { state: { filtres: { cat } } })}>
            ← Accueil
          </button>
        </div>
      </header>

      {/* ══ SIDEBAR + CONTENU ═════════════════════ */}
      <div className="PLayout">

        {/* ── Sidebar catégories ─────────────────── */}
        <aside className="PSidebar">
          <div className="PSidebar__title">Catégories</div>

          <button
            className={`PSidebar__item ${cat === '' ? 'PSidebar__item--active' : ''}`}
            onClick={() => setParam('cat', '')}
          >
            <span className="PSidebar__all">🎸</span>
            <span>Tous les produits</span>
          </button>

          {CATEGORIES.map((c) => (
            <button
              key={c.value}
              className={`PSidebar__item ${cat === c.value ? 'PSidebar__item--active' : ''}`}
              onClick={() => setParam('cat', c.value)}
            >
              <img src={c.img} alt={c.label} className="PSidebar__img" />
              <span>{c.label}</span>
            </button>
          ))}
        </aside>

        {/* ── Zone principale ────────────────────── */}
        <main className="PMain">

          {/* Titre + filtres */}
          <div className="PMain__head">
            <div className="PMain__headLeft">
              <h1 className="PMain__h1">{catLabel}</h1>
              <span className="PMain__count">
                {loading ? '…' : `${filtered.length} produit${filtered.length > 1 ? 's' : ''}`}
              </span>
            </div>

            <div className="PMain__headRight">
              {/* Filtre prix */}
              <select
                className="PSelect"
                value={prix}
                onChange={(e) => setParam('prix', e.target.value)}
              >
                {PRIX_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>

              {/* Reset */}
              {(cat || prix || search) && (
                <button className="PBtn PBtn--ghost" onClick={resetFiltres}>
                  ✕ Effacer
                </button>
              )}
            </div>
          </div>

          {/* Chips catégories (mobile) */}
          <div className="PChips">
            <button
              className={`PChip ${cat === '' ? 'PChip--active' : ''}`}
              onClick={() => setParam('cat', '')}
            >
              Tous
            </button>
            {CATEGORIES.map((c) => (
              <button
                key={c.value}
                className={`PChip ${cat === c.value ? 'PChip--active' : ''}`}
                onClick={() => setParam('cat', c.value)}
              >
                {c.label}
              </button>
            ))}
          </div>

          {/* Grille */}
          {loading ? (
            <div className="PLoader">
              <div className="PLoader__spinner" />
              <p>Chargement des produits…</p>
            </div>
          ) : filtered.length === 0 ? (
            <div className="PEmpty">
              <div className="PEmpty__icon">🎸</div>
              <p>Aucun produit ne correspond à votre recherche.</p>
              <button className="PBtn" onClick={resetFiltres}>Voir tous les produits</button>
            </div>
          ) : (
            <div className="PGrid">
              {filtered.map((object) => (
                <div key={object.id} className="PCardWrap">
                  <Annonce prix={prix} cat={cat} data={object} horaires={horaires} />
                </div>
              ))}
            </div>
          )}
        </main>
      </div>

      {/* ══ FOOTER MINIMAL ═══════════════════════ */}
      <footer className="PFooter">
        <span>© {new Date().getFullYear()} — Guitare &amp; Basse Shop Chalon</span>
        <span>·</span>
        <a href="tel:0663460105">06 63 46 01 05</a>
        <span>·</span>
        <a href="mailto:david.foucard@gmail.com">david.foucard@gmail.com</a>
        <span>·</span>
        <span>12 Rue Pasteur, 71100 Chalon-sur-Saône</span>
      </footer>
    </div>
  );
}
